import { useAtom, useAtomValue } from 'jotai';
import { useAtomCallback } from 'jotai/utils';
import { useCallback } from 'react';
import type { RecorderCursor, RecorderInitiator } from '~/atoms/recorder-atoms';
import { recorderCursorAtom, recorderInitiatorAtom, recorderStateAtom, vmStatusAtom } from '~/atoms/recorder-atoms';
import { useSendVmCommand } from './vm/useSendVmCommand';
import type { VmStatus } from './vm/useVm';

type UpdateParams = {
  vmStatus?: VmStatus;
  recorderCursor?: RecorderCursor;
  recorderInitiator?: RecorderInitiator;
};

export function useLiveStateManager() {
  const [vmStatus, setVmStatus] = useAtom(vmStatusAtom);
  const [recorderCursor, setRecorderCursor] = useAtom(recorderCursorAtom);
  const [recorderInitiator, setRecorderInitiator] = useAtom(recorderInitiatorAtom);
  const recorderState = useAtomValue(recorderStateAtom);

  const sendServerCommand = useSendVmCommand();

  const updateLiveState = useAtomCallback(
    useCallback(
      (get, _set, updater: (prev: UpdateParams) => UpdateParams) => {
        // We must read the previous values from the atoms using the get function.
        // Otherwise it will not be reading the latest values.
        const prevState: UpdateParams = {
          vmStatus: get(vmStatusAtom),
          recorderCursor: get(recorderCursorAtom),
          recorderInitiator: get(recorderInitiatorAtom),
        };

        const newState = updater(prevState);

        // Check if recorderCursor has changed
        const { recorderCursor: prevCursor } = prevState;
        const { recorderCursor: newCursor } = newState;

        // Update the atoms
        setVmStatus(newState.vmStatus);
        setRecorderCursor(newState.recorderCursor);
        setRecorderInitiator(newState.recorderInitiator);

        // Send 'set-cursor' command only if cursor has changed meaningfully
        if (
          newCursor &&
          (prevCursor?.parentContext?.componentId !== newCursor.parentContext?.componentId ||
            prevCursor?.parentContext?.conditional?.conditionalBlockId !==
              newCursor.parentContext?.conditional?.conditionalBlockId ||
            prevCursor?.stepId !== newCursor.stepId ||
            prevCursor?.stepOrder !== newCursor.stepOrder ||
            prevCursor?.operation?.id !== newCursor.operation?.id ||
            prevCursor?.operation?.order !== newCursor.operation?.order)
        ) {
          sendServerCommand({
            type: 'set-cursor',
            parentContext: newCursor.parentContext,
            stepId: newCursor.stepId,
            stepOrder: newCursor.stepOrder,
            operation: newCursor.operation,
          });
        }
      },
      [setVmStatus, setRecorderCursor, setRecorderInitiator, sendServerCommand],
    ),
  );

  // Return only the values and the controlled update function, not the individual setters
  return {
    vmStatus,
    recorderCursor,
    recorderInitiator,
    recorderState,
    updateLiveState,
  };
}
